import ICreateAccountForm from "../../models/view/onboarding/ICreateAccountForm";
import {createField} from "../../factories/fieldFactory";
import {
  createValidationWithMessage,
  validateEmail,
  validateHasAnyValue,
  validatePassword
} from "../validation/validation";
import IErrorsResponse from "../../client/models/common/IErrorsResponse";
import {displayErrorToUser} from "../error/error";
import {createEventBusAndHook} from "../eventBus/common";
import {navigate} from "../navigation/navigation";
import {appPagePaths} from "../../config/config";
import IField from "../../models/view/common/IField";
import {getItem, setItem} from "../device/localStorage";
import productMonitoring from "../log/productMonitoring";
import {userAuthentication} from "../user/authentication";

const hasOnboardingBeenCompletedLocalStorageKey = `hasOnboardingBeenCompleted`;

class Onboarding{
  createAccountForm = createCreateAccountFormModel();
  createAccountLoadingHook = createLoadingHook();

  setupKeyboardLater(){
    this.setIsOnboardingCompleted(true);
    navigate(appPagePaths.bootCampPage);
  }

  getIsOnboardingCompleted(){
    const hasOnboardingBeenCompleted = getItem(hasOnboardingBeenCompletedLocalStorageKey);
    return hasOnboardingBeenCompleted === "true";
  }

  setIsOnboardingCompleted(isCompleted: boolean){
    setItem(hasOnboardingBeenCompletedLocalStorageKey,isCompleted? "true" : "false");
  }

  completeOnboarding(): void {
    this.setIsOnboardingCompleted(true);
    navigate(appPagePaths.bootCampPage);
  }

  getCreateAccountForm(){
    return this.createAccountForm;
  }

  useCreateAccountLoadingHook(){
    return this.createAccountLoadingHook.use();
  }

  /**
   * Use the createAccountForm to perform clientside validations, and then to send a request to the create-account endpoint.
   * Update the UI with any validation errors from the clientside validation or received from the service.
   * Do not perform api call unless all clientside validations pass.
   */
  async createAccountUsingCreateAccountForm(isMicrophoneAvailable: boolean){
    // Guard: Validate form - validation will result in events/hooks firing to update the form
    const isValid = validateCreateAccountForm(this.createAccountForm);
    if(!isValid) {
      return;
    }

    const {email, password} = getCreateAccountFormValues(this.createAccountForm);

    try {
      // Attempt calling the create account endpoint
      void await userAuthentication.signUp({email, password});

      const redirectTo = isMicrophoneAvailable
        ? appPagePaths.onboarding_setupFeedback_setupFeedbackAndScoring
        : appPagePaths.setupYourKeyboardPage;
      navigate(redirectTo);

    } catch(e) {
      const error = e as Error;
      if(error.name === "ErrorsResponse") { //if the exception is ErrorsResponse, then this means the service responded with some validations that can be corrected by the user.
        const errorsResponse = error as IErrorsResponse;
        updateValidationMessagesBasedOnAPIResponse(this.createAccountForm, errorsResponse);
      } else { //otherwise, this is an unknown error and we should display a modal error dialog to the user.
        productMonitoring.logException(error);
        displayErrorToUser("", [error.message]);
      }
    }
  }
}

/**
 * Iterate over each property/key of the ICreateAccountForm and see if the key exists as a paramName in the IErrorsResponse returned from the API.
 * If so, update the form field's error messages and emit a change so the UI is updated.
 * @param createAccountForm - form field collection
 * @param errorsResponse - response received from the API
 */
function updateValidationMessagesBasedOnAPIResponse(createAccountForm: ICreateAccountForm, errorsResponse: IErrorsResponse){
  const keys = Object.keys(createAccountForm);
  for(let key of keys){
    //@ts-ignore
    const formField = createAccountForm[key] as IField;
    if(formField.error){ //make sure we cast things correctly, just in case someone adds a field that's not a form field.
      const errorMessages = getErrorMessagesForParamFromAPIResponse(key, errorsResponse);
      const isInErrorState = errorMessages.length > 0;
      formField.error.isInErrorState = isInErrorState;
      formField.error.errorMessages = errorMessages;
      formField.emitMessage({...formField});//update the ui.
    }
  }
}

interface ILoading{
  isLoading: boolean;
}

function createLoadingHook(){
  const {use, emitMessage: emitLoadingChanged} = createEventBusAndHook<ILoading>({isLoading: false});
  return {use, emitLoadingChanged};
}

/**
 * Find the error messages for a given paramName.  e.g. "firstName" might have an error from the server because it contains special characters, etc.
 * @param paramName - name of the param to search for in the errors response.  e.g. "username"
 * @param errorsResponse - response object received from the api when there are issues/bad request.
 */
function getErrorMessagesForParamFromAPIResponse(paramName: string, errorsResponse: IErrorsResponse){
  const result: string[] = [];
  if(errorsResponse && errorsResponse.errors){
    const errorsForParamName = errorsResponse.errors.filter(e => e.paramName === paramName);
    const messages = errorsForParamName.map(e => e.message);
    for(let message of messages){
      if(message){
        result.push(message);
      }
    }
  }
  return result;
}

/**
 * Get the actual values of the ICreateAccountForm so they can be sent as part of the request to the create account endpoint.
 * @param createAccountForm
 */
function getCreateAccountFormValues(createAccountForm: ICreateAccountForm){
  return {
    email: createAccountForm.email.value,
    password: createAccountForm.password.value,
  }
}

/**
 * Validate each form field on the ICreateAccountForm.  Useful for performing all validations when Create Account button is clicked.
 * @param createAccountForm
 */
function validateCreateAccountForm(createAccountForm: ICreateAccountForm){
  const isEmailValid = createAccountForm.email.validate();
  const isPasswordValid = createAccountForm.password.validate();
  const isValid = isEmailValid && isPasswordValid;
  return isValid;
}

/**
 * Create the ICreateAccountForm by creating each of its formFields and associated validations.
 */
function createCreateAccountFormModel(){
  const createAccountForm: ICreateAccountForm = {
    email: createField("", [createValidationWithMessage(validateEmail, "Email address must be a valid email address")]),
    password: createField("", [createValidationWithMessage(validatePassword, "8 characters or more required for password")]),
  }
  return createAccountForm;
}



const onboarding = new Onboarding();
export default onboarding;